// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes
const TOKEN_KEY = "token";
const USER_KEY  = "user";
const API_BASE    = process.env.REACT_APP_API_URL || "/api/v2";

export function AuthProvider({ children }) {
  const navigate        = useNavigate();
  const timerRef        = useRef(null);
  const warningTimerRef = useRef(null);

  const [user,          setUser]          = useState(null);
  const [token,         setToken]         = useState(null);
  const [loading,       setLoading]       = useState(true);  // checking stored session
  const [sessionWarning, setSessionWarning] = useState(false); // show "still there?" banner

  // ── LOGOUT ───────────────────────────────────────────────────────────
  const logout = useCallback((reason = "manual") => {
    clearTimeout(timerRef.current);
    clearTimeout(warningTimerRef.current);

    // Clear all stored state
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.clear();

    setUser(null);
    setToken(null);
    setSessionWarning(false);

    console.log(`[Auth] Logged out — reason: ${reason}`);
    navigate("/login", { replace: true, state: { reason } });
  }, [navigate]);

  // ── INACTIVITY TIMER ─────────────────────────────────────────────────
  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warningTimerRef.current);
    setSessionWarning(false);

    // Show warning banner at 4 min (1 min before logout)
    warningTimerRef.current = setTimeout(() => {
      setSessionWarning(true);
    }, INACTIVITY_LIMIT - 60 * 1000);

    // Auto-logout at 5 min
    timerRef.current = setTimeout(() => {
      logout("inactivity");
    }, INACTIVITY_LIMIT);
  }, [logout]);

  // ── BIND ACTIVITY EVENTS ─────────────────────────────────────────────
  useEffect(() => {
    if (!token) return; // only track when logged in

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately on login

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [token, resetTimer]);

  // ── RESTORE SESSION ON PAGE LOAD ─────────────────────────────────────
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser  = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      try {
        // Check token expiry from JWT payload
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        const isExpired = payload.exp && Date.now() / 1000 > payload.exp;

        if (isExpired) {
          console.log("[Auth] Stored token expired — clearing session");
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        } else {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  // ── LOGIN ─────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
      throw new Error(data.message || "Login failed");
    }

    const { token: newToken, user: newUser } = data.data;

    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);

    return newUser;
  }, []);

  // ── EXTEND SESSION (from warning banner) ─────────────────────────────
  const extendSession = useCallback(() => {
    resetTimer();
    setSessionWarning(false);
  }, [resetTimer]);

  // ── AUTH FETCH HELPER (auto-attaches token + handles 401) ────────────
  const authFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 401) {
      logout("token_expired");
      throw new Error("Session expired. Please log in again.");
    }

    return res;
  }, [token, logout]);

  const value = {
    user,
    token,
    loading,
    sessionWarning,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    extendSession,
    authFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}